import React from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col,
  Spinner, Alert, Badge,
} from 'react-bootstrap';
import {
  PlusIcon, PencilSquareIcon, TrashIcon,
  ClipboardDocumentListIcon, CheckCircleIcon, XMarkIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanyPurchaseOrders, STATUS_LABELS, STATUS_COLORS } from './hooks/useCompanyPurchaseOrders';

const thStyle: React.CSSProperties = {
  color: '#475569', fontWeight: 600, fontSize: '0.8rem',
  textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px',
};

const CompanyPurchaseOrders: React.FC = () => {
  const {
    loading, suppliers, allIngredients, statusFilter, setStatusFilter,
    filtered, orders, feedback, setFeedback,
    showOrderForm, setShowOrderForm, editingOrder, orderForm, setOrderForm,
    orderItems, saving, receiveModal, setReceiveModal,
    deleteState, setDeleteState,
    openCreate, openEdit, addItem, removeItem, updateItem,
    handleSaveOrder, openReceive, handleReceive, handleDelete,
  } = useCompanyPurchaseOrders();

  if (loading) {
    return (
      <CompanyLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading orders...</p>
        </Container>
      </CompanyLayout>
    );
  }

  // ── Order form view ──────────────────────────────────────────────────────
  if (showOrderForm) {
    const selectedSupplierName = suppliers.find(s => String(s.id) === orderForm.supplier)?.name || '';
    return (
      <CompanyLayout>
        <Container fluid className="py-4 px-3 px-md-4 page-content-padding" style={{ maxWidth: '860px' }}>
          <div className="d-flex align-items-center gap-2 mb-4">
            <Button variant="link" className="p-0 text-muted" onClick={() => setShowOrderForm(false)}>
              ← Back
            </Button>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
              {editingOrder ? `Edit Order #${editingOrder.id}` : 'New Order'}
            </h4>
          </div>

          {feedback.error && <Alert variant="danger" className="mb-3">{feedback.error}</Alert>}

          {/* Supplier */}
          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
            <Card.Body>
              <h6 className="fw-bold mb-3" style={{ color: '#1e293b' }}>Supplier</h6>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Supplier <span className="text-danger">*</span></Form.Label>
                    <Form.Select size="sm" value={orderForm.supplier} onChange={e => setOrderForm(p => ({ ...p, supplier: e.target.value }))}>
                      <option value="">Select a supplier</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Status</Form.Label>
                    <Form.Select size="sm" value={orderForm.status} onChange={e => setOrderForm(p => ({ ...p, status: e.target.value }))}>
                      {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Order details */}
          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
            <Card.Body>
              <h6 className="fw-bold mb-3" style={{ color: '#1e293b' }}>Order Details</h6>
              <Row className="g-3">
                <Col xs={12} md={5}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Expected Delivery Date</Form.Label>
                    <Form.Control size="sm" type="date" value={orderForm.expected_delivery_date}
                      onChange={e => setOrderForm(p => ({ ...p, expected_delivery_date: e.target.value }))} />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Notes</Form.Label>
                    <Form.Control as="textarea" rows={2} size="sm" value={orderForm.notes}
                      onChange={e => setOrderForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Additional notes for this order..." />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Line items */}
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: '#1e293b' }}>
                  <ClipboardDocumentListIcon style={{ width: '18px', height: '18px', color: '#059669' }} />
                  Line Items
                </h6>
                <Button variant="outline-success" size="sm" onClick={addItem} disabled={!orderForm.supplier}
                  style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
                  <PlusIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
                  Add Item
                </Button>
              </div>

              {!orderForm.supplier && (
                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Select a supplier to add items.</p>
              )}

              {orderItems.length > 0 && (
                <div className="table-responsive">
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>Ingredient</th>
                        <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', width: '120px' }}>Qty</th>
                        <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem', width: '110px' }}>Unit Price</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Select size="sm" value={item.ingredient} onChange={e => updateItem(idx, 'ingredient', e.target.value)}>
                              <option value="">Select ingredient</option>
                              {allIngredients.map((i: any) => (
                                <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" min="0" step="0.001" placeholder="0"
                              value={item.quantity_ordered} onChange={e => updateItem(idx, 'quantity_ordered', e.target.value)} />
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" min="0" step="0.0001" placeholder="—"
                              value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} />
                          </td>
                          <td>
                            <Button variant="link" className="p-0 text-danger" onClick={() => removeItem(idx)} title="Remove">
                              <XMarkIcon style={{ width: '16px', height: '16px' }} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => setShowOrderForm(false)} disabled={saving}>Cancel</Button>
            <Button variant="success" size="sm" onClick={handleSaveOrder} disabled={saving}>
              {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</> : (editingOrder ? 'Save Changes' : 'Save Draft')}
            </Button>
          </div>
        </Container>
      </CompanyLayout>
    );
  }

  // ── Order list view ──────────────────────────────────────────────────────
  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {feedback.success && (
          <Alert variant="success" dismissible onClose={() => setFeedback(p => ({ ...p, success: '' }))} className="mb-3">
            {feedback.success}
          </Alert>
        )}
        {feedback.error && (
          <Alert variant="danger" dismissible onClose={() => setFeedback(p => ({ ...p, error: '' }))} className="mb-3">
            {feedback.error}
          </Alert>
        )}

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardDocumentListIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Purchase Orders</h4>
            <Badge bg="" style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.75rem', fontWeight: '600', padding: '4px 10px', borderRadius: '6px' }}>
              {orders.length}
            </Badge>
          </div>
          <Button variant="success" size="sm" className="d-flex align-items-center gap-1"
            onClick={openCreate} style={{ padding: '6px 14px' }}>
            <PlusIcon style={{ width: '15px', height: '15px' }} />
            New Order
          </Button>
        </div>

        {/* Status filter tabs */}
        <div className="d-flex gap-2 flex-wrap mb-4">
          {[
            { key: 'all',       label: 'All',       accent: '#059669', activeBg: '#059669', activeColor: '#fff' },
            { key: 'draft',     label: 'Draft',     accent: '#94a3b8', activeBg: '#f1f5f9', activeColor: '#334155' },
            { key: 'sent',      label: 'Sent',      accent: '#3b82f6', activeBg: '#dbeafe', activeColor: '#1d4ed8' },
            { key: 'confirmed', label: 'Confirmed', accent: '#10b981', activeBg: '#dcfce7', activeColor: '#166534' },
            { key: 'received',  label: 'Received',  accent: '#8b5cf6', activeBg: '#ede9fe', activeColor: '#5b21b6' },
            { key: 'cancelled', label: 'Cancelled', accent: '#ef4444', activeBg: '#fee2e2', activeColor: '#991b1b' },
          ].map(({ key, label, accent, activeBg, activeColor }) => {
            const count = key === 'all' ? orders.length : orders.filter(o => o.status === key).length;
            const active = statusFilter === key;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: '2px', padding: '10px 18px', minWidth: '82px',
                  border: active ? `2px solid ${accent}` : '2px solid #e2e8f0',
                  borderRadius: '12px', cursor: 'pointer', transition: 'all 0.15s',
                  backgroundColor: active ? activeBg : '#fff',
                  boxShadow: active ? `0 2px 8px ${accent}30` : 'none',
                  outline: 'none',
                }}
              >
                <span style={{
                  fontSize: '1.3rem', fontWeight: 800, lineHeight: 1,
                  color: active ? (key === 'all' ? '#fff' : activeColor) : '#1e293b',
                }}>
                  {count}
                </span>
                <span style={{
                  fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  color: active ? (key === 'all' ? 'rgba(255,255,255,0.85)' : activeColor) : '#6b7280',
                }}>
                  {label}
                </span>
                {active && (
                  <span style={{
                    display: 'block', width: '20px', height: '3px',
                    borderRadius: '2px', backgroundColor: accent, marginTop: '4px',
                  }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          {filtered.length === 0 ? (
            <Card.Body className="text-center py-5 text-muted">
              <ClipboardDocumentListIcon style={{ width: '36px', height: '36px', opacity: 0.3, marginBottom: '8px' }} />
              <div>{orders.length === 0 ? 'No purchase orders yet.' : 'No orders match this filter.'}</div>
              {orders.length === 0 && (
                <Button variant="success" size="sm" className="mt-3" onClick={openCreate}>Create First Order</Button>
              )}
            </Card.Body>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Supplier</th>
                    <th style={thStyle} className="d-none d-md-table-cell">Items</th>
                    <th style={thStyle} className="d-none d-lg-table-cell">Expected</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle} className="d-none d-md-table-cell">Total</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(order => {
                    const sc = STATUS_COLORS[order.status] || STATUS_COLORS.draft;
                    const canReceive = order.status === 'sent' || order.status === 'confirmed';
                    return (
                      <tr key={order.id}>
                        <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '0.85rem' }}>#{order.id}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 500, color: '#1e293b' }}>{order.supplier_name || '—'}</td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                          {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '0.85rem' }} className="d-none d-lg-table-cell">
                          {order.expected_delivery_date || '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Badge bg="" style={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 600, fontSize: '0.75rem' }}>
                            {order.status_display}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                          {parseFloat(order.total_value) > 0 ? parseFloat(order.total_value).toFixed(2) : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div className="d-flex gap-1">
                            {canReceive && (
                              <Button variant="outline-success" size="sm" onClick={() => openReceive(order)}
                                style={{ padding: '4px 8px' }} title="Receive delivery">
                                <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
                              </Button>
                            )}
                            {order.status !== 'received' && (
                              <Button variant="outline-secondary" size="sm" onClick={() => openEdit(order)}
                                style={{ padding: '4px 8px' }} title="Edit">
                                <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                              </Button>
                            )}
                            {(order.status === 'draft' || order.status === 'cancelled') && (
                              <Button variant="outline-danger" size="sm"
                                onClick={() => setDeleteState({ showModal: true, order, deleting: false })}
                                style={{ padding: '4px 8px' }} title="Delete">
                                <TrashIcon style={{ width: '14px', height: '14px' }} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </Container>

      {/* Receive Delivery Modal */}
      <Modal show={receiveModal.show} onHide={() => setReceiveModal(p => ({ ...p, show: false }))} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 600 }}>
            Receive Delivery — Order #{receiveModal.order?.id} from {receiveModal.order?.supplier_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
            Confirm the quantities received. Stock levels will be updated automatically.
          </p>
          <Table size="sm" className="align-middle">
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.8rem' }}>Ingredient</th>
                <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.8rem' }}>Ordered</th>
                <th style={{ color: '#64748b', fontWeight: 600, fontSize: '0.8rem' }}>Received</th>
              </tr>
            </thead>
            <tbody>
              {receiveModal.order?.items.map(item => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 500 }}>{item.ingredient_name}</td>
                  <td style={{ color: '#64748b' }}>{item.quantity_ordered} {item.ingredient_unit}</td>
                  <td>
                    <Form.Control size="sm" type="number" min="0" step="0.001" style={{ maxWidth: '120px' }}
                      value={item.id != null ? (receiveModal.receivedQtys[item.id] ?? item.quantity_ordered) : item.quantity_ordered}
                      onChange={e => item.id != null && setReceiveModal(p => ({
                        ...p,
                        receivedQtys: { ...p.receivedQtys, [item.id!]: e.target.value }
                      }))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setReceiveModal(p => ({ ...p, show: false }))} disabled={receiveModal.saving}>
            Cancel
          </Button>
          <Button variant="success" size="sm" onClick={handleReceive} disabled={receiveModal.saving}>
            {receiveModal.saving ? <><Spinner size="sm" animation="border" className="me-1" />Processing...</> : 'Confirm Receipt'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={deleteState.showModal} onHide={() => setDeleteState(p => ({ ...p, showModal: false }))} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>Delete Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Delete <strong>Order #{deleteState.order?.id}</strong>? This cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setDeleteState(p => ({ ...p, showModal: false }))} disabled={deleteState.deleting}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleteState.deleting}>
            {deleteState.deleting ? <><Spinner size="sm" animation="border" className="me-1" />Deleting...</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
};

export default CompanyPurchaseOrders;
